import React, { useRef, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, Box, Stack, Typography, Alert, Chip,
} from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import { parse as parseYaml } from 'yaml';

// Upload or paste an OpenAPI 3.x spec (JSON or YAML). Parses client-side, shows a
// quick summary, and inserts an apiSpec node that renders a full auto-generated
// reference (editor NodeView preview + docs-site expansion).
export function ApiSpecDialog({
  open, onClose, onSave,
}: { open: boolean; onClose: () => void; onSave: (spec: any) => void }) {
  const [text, setText] = useState('');
  const [err, setErr] = useState('');
  const fileRef = useRef<HTMLInputElement>(null);

  const parseSpec = (raw: string): any | null => {
    const t = raw.trim();
    if (!t) return null;
    try { return JSON.parse(t); } catch { /* try yaml */ }
    try { return parseYaml(t); } catch { return null; }
  };

  const spec = parseSpec(text);
  const valid = spec && typeof spec === 'object' && spec.paths && Object.keys(spec.paths).length > 0;
  const endpointCount = valid
    ? Object.values<any>(spec.paths).reduce((n, item) => n + Object.keys(item).filter((k) => ['get', 'post', 'put', 'patch', 'delete', 'head', 'options'].includes(k)).length, 0)
    : 0;

  const onFile = async (files: FileList | null) => {
    const f = files?.[0];
    if (!f) return;
    setText(await f.text());
    setErr('');
    if (fileRef.current) fileRef.current.value = '';
  };

  const insert = () => {
    if (!valid) { setErr('Not a valid OpenAPI 3.x spec (no paths found).'); return; }
    onSave(spec);
    setText('');
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Import OpenAPI spec</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <Box>
            <input ref={fileRef} type="file" accept=".json,.yaml,.yml,application/json,text/yaml" hidden onChange={(e) => onFile(e.target.files)} />
            <Button variant="outlined" startIcon={<UploadFileIcon />} onClick={() => fileRef.current?.click()}>Upload .json / .yaml</Button>
            <Typography variant="caption" color="text.secondary" sx={{ ml: 1.5 }}>or paste below</Typography>
          </Box>
          <TextField
            value={text} onChange={(e) => { setText(e.target.value); setErr(''); }}
            multiline minRows={10} fullWidth placeholder={'{\n  "openapi": "3.0.0",\n  "info": { "title": "My API", "version": "1.0" },\n  "paths": { … }\n}'}
            sx={{ '& textarea': { fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontSize: 12.5 } }}
          />
          {err ? <Alert severity="error">{err}</Alert> : null}
          {text.trim() && !valid ? <Alert severity="warning">Couldn’t parse a valid OpenAPI spec yet.</Alert> : null}
          {valid ? (
            <Alert severity="success" icon={false}>
              <Stack direction="row" spacing={1} alignItems="center">
                <Chip size="small" color="success" label="Valid" />
                <Typography variant="body2"><b>{spec.info?.title || 'API'}</b> v{spec.info?.version || '?'} · {endpointCount} endpoint{endpointCount === 1 ? '' : 's'}</Typography>
              </Stack>
            </Alert>
          ) : null}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" disabled={!valid} onClick={insert}>Insert reference</Button>
      </DialogActions>
    </Dialog>
  );
}
